import { FC } from 'react';
import { FunctionField, Link, SelectField, TextField, WithRecord } from 'react-admin';

import { GetBoInvestorProfileIdEndpoint } from '@Common/types/bo/b2b/investor_profile';

import { investorProfileRiskProfileChoices } from '@sh4res/bo-common/choices/investorProfile';
import { ShowFields, ShowSection, ShowWithNavigation } from '@sh4res/bo-common/components';
import { IdField } from '@sh4res/bo-common/fields';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { B2bInvestorProfileShowGridWrapper } from '@sh4res/bo-common/resources/investor_profile/components/B2bInvestorProfileShowGridWrapper/B2bInvestorProfileShowGridWrapper';
import { getB2BInvestorProfileResourceReference } from '@sh4res/bo-common/resources/investor_profile/helpers/getB2BInvestorProfileResourceReference';
import { buildLink } from '@sh4res/bo-common/utils/build_link';

export const B2bInvestorProfileShow: FC = () => {
  const resourceBasePath = useResourceBasePath();
  return (
    <ShowWithNavigation
      actions={false}
      navigateTo={() => -1}
      title={
        <WithRecord
          render={(record: GetBoInvestorProfileIdEndpoint['Client']['ResponseBody']) => record.name}
        />
      }
      sx={{ height: '100%' }}>
      <ShowSection label="general.sections.details">
        <ShowFields<GetBoInvestorProfileIdEndpoint['Client']['ResponseBody']>
          fields={{
            id: (
              <IdField
                reference={getB2BInvestorProfileResourceReference(resourceBasePath)}
                data-testid="BoExternalB2bInvestorProfileShow_IdField"
              />
            ),
            b2b_contract_id: (
              <IdField
                reference={`${resourceBasePath}contracts`}
                data-testid={'BoExternalB2bInvestorProfileShow_B2bContractIdField'}
              />
            ),
            name: <TextField data-testid="BoExternalB2bInvestorProfileShow_NameTextInput" />,
            plan_category: (
              <TextField
                label="resources.bo_common.b2b.investor_profile.list.plan_category"
                data-testid="BoExternalB2bInvestorProfileShow_PlanCategoryTextInput"
              />
            ),
            allocation_instruments: (
              <FunctionField
                label="resources.bo_common.b2b.investor_profile.list.allocation_nbr"
                render={(record: GetBoInvestorProfileIdEndpoint['Client']['ResponseBody']) =>
                  record.allocation_instruments.length
                }
                data-testid="BoExternalB2bInvestorProfileShow_AllocationInstrumentsFunctionField"
              />
            ),
            plan_ids: (
              <FunctionField
                label="resources.bo_common.b2b.investor_profile.list.plan_ids"
                render={(record: GetBoInvestorProfileIdEndpoint['Client']['ResponseBody']) =>
                  record.plan_ids.length > 0 ? (
                    <Link
                      to={buildLink(
                        `${resourceBasePath}contracts`,
                        'show',
                        String(record.b2b_contract_id),
                        { tab: 'plans' },
                      )}
                      target="_blank"
                      rel="noopener noreferrer">
                      {record.plan_ids.join(', ')}
                    </Link>
                  ) : (
                    '-'
                  )
                }
                data-testid="BoExternalB2bInvestorProfileShow_PlanIdsFunctionField"
              />
            ),
            risk_profile: (
              <SelectField
                choices={investorProfileRiskProfileChoices}
                data-testid="BoExternalB2bInvestorProfileShow_RiskProfileField"
              />
            ),
          }}
        />
      </ShowSection>
      <WithRecord
        render={(record: GetBoInvestorProfileIdEndpoint['Client']['ResponseBody']) => (
          <ShowSection label="resources.bo_common.b2b.investor_profile.fields.allocations">
            <B2bInvestorProfileShowGridWrapper
              allocation={record.allocations}
              productCategory={record.plan_category}
              profileType={record.risk_profile}
            />
          </ShowSection>
        )}
      />
    </ShowWithNavigation>
  );
};
